import { oc } from "@orpc/contract";
import { z } from "zod";

const backfillRunSchema = z.object({
  id: z.string(),
  backfillName: z.string(),
  status: z.enum([
    "pending",
    "running",
    "completed",
    "failed",
    "cancelled",
    "interrupted",
  ]),
  orgId: z.string().nullable(),
  dryRun: z.boolean(),
  limit: z.number().nullable(),
  chunkSize: z.number().nullable(),
  processed: z.number(),
  total: z.number().nullable(),
  startedAt: z.string().nullable(),
  completedAt: z.string().nullable(),
  error: z.string().nullable(),
  triggeredByUserId: z.string().nullable(),
  triggeredBySource: z.enum(["user", "control-plane"]),
  triggeredByLabel: z.string().nullable(),
  createdAt: z.string(),
});

export const backfillsContracts = {
  listBackfills: oc
    .input(z.object({}))
    .output(
      z.object({
        executionTargets: z.array(
          z.object({
            target: z.enum(["worker", "render"]),
            available: z.boolean(),
            missingRequirements: z.array(z.string()),
          }),
        ),
        items: z.array(
          z.object({
            name: z.string(),
            description: z.string(),
            chunkSize: z.number().nullable(),
            chunkTimeout: z.number().nullable(),
            params: z.array(
              z.object({
                key: z.string(),
                required: z.boolean(),
                default: z.string().nullable(),
                description: z.string().nullable(),
              }),
            ),
            lastRun: backfillRunSchema.nullable(),
          }),
        ),
      }),
    )
    .route({ method: "POST" }),

  triggerBackfill: oc
    .input(
      z.object({
        backfillName: z.string(),
        executionTarget: z.enum(["worker", "render"]),
        orgId: z.string().optional(),
        dryRun: z.boolean(),
        limit: z.number().int().positive().optional(),
        /** Free-form key/value params passed through to the backfill handler. */
        params: z.record(z.string(), z.string()).optional(),
      }),
    )
    .output(
      z.object({
        runId: z.string(),
      }),
    )
    .route({ method: "POST" }),

  listBackfillRuns: oc
    .input(
      z.object({
        backfillName: z.string().optional(),
        page: z.number().int().positive().default(1),
        pageSize: z.number().int().positive().max(100).default(20),
      }),
    )
    .output(
      z.object({
        items: z.array(backfillRunSchema),
        total: z.number(),
      }),
    )
    .route({ method: "POST" }),

  getBackfillRun: oc
    .input(
      z.object({
        runId: z.string(),
      }),
    )
    .output(backfillRunSchema)
    .route({ method: "POST" }),

  estimateBackfill: oc
    .input(
      z.object({
        backfillName: z.string(),
        orgId: z.string().optional(),
      }),
    )
    .output(
      z.object({
        total: z.number().nullable(),
      }),
    )
    .route({ method: "POST" }),

  markRunFailed: oc
    .input(
      z.object({
        runId: z.string(),
      }),
    )
    .output(
      z.object({
        success: z.boolean(),
      }),
    )
    .route({ method: "POST" }),
};
